import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Phone, MessageCircle, ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHONE = "1164353344";
const EMAIL = "melinadmkt@gmail.com";
const WHATSAPP_LINK = `https://wa.me/549${PHONE}?text=${encodeURIComponent(
  "¡Hola Melina! Vi tu landing y quiero saber más sobre tus servicios."
)}`;

const SERVICES = [
  "Auditoría de Redes Sociales y Web",
  "Plan de Negocios",
  "Configuración de Meta Ads",
  "Creación de Landing Page",
  "Creación de E-commerce",
  "Otro / No lo sé aún",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      setForm((f) => ({ ...f, service: e.detail || "" }));
    };
    window.addEventListener("preselect-service", handler);
    return () => window.removeEventListener("preselect-service", handler);
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Por favor completá los campos requeridos.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        service: form.service || null,
        message: form.message.trim(),
      });
      toast.success("¡Mensaje enviado! Te contesto a la brevedad ✨");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(", ")
        : detail || "No pudimos enviar tu mensaje. Probá de nuevo.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="bg-white py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-6">
            <p className="text-xs tracking-[0.3em] uppercase text-[#475569] mb-5">
              — 04 / Contacto
            </p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#0A192F]">
              Hablemos de tu <span className="italic">próximo paso</span>.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-base sm:text-lg text-[#475569] leading-relaxed">
              Contame qué necesitás y diseñamos juntas el camino. Respondo todos
              los mensajes personalmente.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* LEFT: Form */}
          <motion.form
            onSubmit={onSubmit}
            data-testid="contact-form"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
            className="lg:col-span-7 space-y-8"
          >
            <div>
              <label className="block text-[10px] uppercase tracking-[0.3em] text-[#475569] mb-2">
                Tu nombre *
              </label>
              <input
                type="text"
                required
                value={form.name}
                onChange={update("name")}
                data-testid="contact-input-name"
                placeholder="Cómo te llamás"
                className="w-full bg-transparent border-0 border-b border-[#0A192F] py-3 text-base text-[#0A192F] placeholder:text-[#9CA3AF] focus:outline-none focus:border-b-2"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-[#475569] mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  data-testid="contact-input-email"
                  placeholder="tucorreo@ejemplo.com"
                  className="w-full bg-transparent border-0 border-b border-[#0A192F] py-3 text-base text-[#0A192F] placeholder:text-[#9CA3AF] focus:outline-none focus:border-b-2"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-[#475569] mb-2">
                  Teléfono (opcional)
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  data-testid="contact-input-phone"
                  placeholder="+54 9 11 0000 0000"
                  className="w-full bg-transparent border-0 border-b border-[#0A192F] py-3 text-base text-[#0A192F] placeholder:text-[#9CA3AF] focus:outline-none focus:border-b-2"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-[0.3em] text-[#475569] mb-2">
                ¿Qué servicio te interesa?
              </label>
              <select
                value={form.service}
                onChange={update("service")}
                data-testid="contact-input-service"
                className="w-full bg-transparent border-0 border-b border-[#0A192F] py-3 text-base text-[#0A192F] focus:outline-none focus:border-b-2 appearance-none cursor-pointer"
              >
                <option value="">Elegí una opción</option>
                {SERVICES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-[0.3em] text-[#475569] mb-2">
                Tu mensaje *
              </label>
              <textarea
                required
                rows={5}
                value={form.message}
                onChange={update("message")}
                data-testid="contact-input-message"
                placeholder="Contame sobre tu proyecto, etapa, objetivos…"
                className="w-full bg-transparent border-0 border-b border-[#0A192F] py-3 text-base text-[#0A192F] placeholder:text-[#9CA3AF] focus:outline-none focus:border-b-2 resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-button"
              className="btn-lift inline-flex items-center gap-3 bg-[#0A192F] text-white px-8 py-4 text-xs uppercase tracking-[0.25em] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Enviando…
                </>
              ) : (
                <>
                  Enviar mensaje
                  <ArrowUpRight size={16} strokeWidth={1.5} />
                </>
              )}
            </button>
          </motion.form>

          {/* RIGHT: Direct contact */}
          <motion.aside
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.2, 0.8, 0.2, 1] }}
            className="lg:col-span-5 lg:pl-8 lg:border-l border-[#E2E8F0] space-y-10"
          >
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              data-testid="whatsapp-link"
              className="btn-lift group flex items-center justify-between bg-[#25D366] text-white px-6 py-5 w-full"
            >
              <div className="flex items-center gap-4">
                <MessageCircle size={22} strokeWidth={1.5} />
                <div className="text-left">
                  <div className="text-[10px] uppercase tracking-[0.3em] opacity-80">
                    WhatsApp directo
                  </div>
                  <div className="font-display text-xl">+54 9 11 6435 3344</div>
                </div>
              </div>
              <ArrowUpRight
                size={22}
                strokeWidth={1.5}
                className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
              />
            </a>

            <div className="space-y-1">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#475569]">
                Email
              </p>
              <a
                href={`mailto:${EMAIL}`}
                data-testid="email-link"
                className="link-underline font-display text-2xl sm:text-3xl text-[#0A192F] flex items-center gap-2"
              >
                <Mail size={22} strokeWidth={1.5} /> {EMAIL}
              </a>
            </div>

            <div className="space-y-1">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#475569]">
                Teléfono
              </p>
              <a
                href={`tel:+549${PHONE}`}
                data-testid="phone-link"
                className="link-underline font-display text-2xl text-[#0A192F] flex items-center gap-2"
              >
                <Phone size={20} strokeWidth={1.5} /> +54 9 11 6435-3344
              </a>
            </div>

            <div className="pt-8 border-t border-[#E2E8F0]">
              <p className="text-sm text-[#475569] leading-relaxed">
                Suelo responder dentro de las{" "}
                <span className="text-[#0A192F] font-medium">24 horas</span>.
                Si tu proyecto es urgente, escribime directo por WhatsApp.
              </p>
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}
